import { FormSchemaDropdownInput } from "#src/batteries-included-components/Dropdowns/FormSchemaDropdownInput";
import { useGetOneFormSchema } from "#src/components/hooks/adapters/useFormSchema";
import {
  Banner,
  Card,
  Checkbox,
  FilterBuilder,
  FilterBuilderFieldsType,
  Form,
  KeyValueList,
  useForm,
} from "@validereinc/common-components";
import { FormSchemaType, WorkflowTriggerFormCreate } from "@validereinc/domain";
import classNames from "classnames/bind";
import React, { useEffect, useState } from "react";
import { useDeepCompareEffectNoCheck } from "use-deep-compare-effect";
import { useVerifyNewFormSchema } from "../Utils/useVerifyNewFormSchema";
import { useWorkflowConfigurationFormStore } from "../WorkflowConfigurationFormStore";
import styles from "./WorkflowConfigurationSubmitForm.module.scss";

const cx = classNames.bind(styles);

const singleAnswerFilterToFilterCompatibleValue = (
  condition: object
): object => {
  if (
    "answer.value" in condition &&
    "question_id" in condition &&
    typeof condition.question_id === "string"
  ) {
    return { [condition.question_id]: condition["answer.value"] };
  }
  return {};
};

const processConditions = (input: object): object => {
  if ("$or" in input || "$and" in input) {
    let conditions: object[] = [];

    let operator: "$and" | "$or" | undefined = undefined;

    if ("$or" in input && Array.isArray(input.$or)) {
      conditions = input.$or;
      operator = "$or";
    }

    if ("$and" in input && Array.isArray(input.$and)) {
      conditions = input.$and;
      operator = "$and";
    }

    const processedConditions = conditions
      .map((condition) => {
        if (
          "answer.value" in condition &&
          "question_id" in condition &&
          typeof condition.question_id === "string"
        ) {
          return singleAnswerFilterToFilterCompatibleValue(condition);
        }
        return;
      })
      .filter((e) => !!e);

    if (operator) {
      if (processedConditions.length > 1)
        return { [operator]: processedConditions };
      if (processedConditions.length === 1) return processedConditions[0];
    }
  }

  if (
    "answer.value" in input &&
    "question_id" in input &&
    typeof input.question_id === "string"
  ) {
    return singleAnswerFilterToFilterCompatibleValue(input);
  }

  return {};
};

const answerFilterToFilterBuilderFilter = (answerFilter: object): object => {
  const conditions: object[] = Array.isArray(answerFilter)
    ? answerFilter
    : [answerFilter];

  if (conditions.length > 1) {
    return { $and: conditions.map(processConditions) };
  }

  if (conditions.length === 1) return processConditions(conditions[0]);

  return {};
};

const formSchemaConfigToFilterBuilderFields = (
  formSchemaConfig: FormSchemaType["config"]
): FilterBuilderFieldsType => {
  const questionIdToSectionNameMap: Record<string, string> = {};
  const output: FilterBuilderFieldsType = {};

  formSchemaConfig.sections.forEach((section) => {
    section.questions.forEach((q) => {
      questionIdToSectionNameMap[q] = section.name;
    });
  });

  Object.entries(formSchemaConfig.questions).forEach(
    ([questionKey, questionDefinition]) => {
      if (questionDefinition.type === "question") {
        const questionDataType = questionDefinition.data_type;
        const display_name = `${questionIdToSectionNameMap[questionKey]} -> ${questionDefinition.prompt}`;
        if (questionDataType === "string") {
          output[questionKey] = {
            data_type: "string",
            display_name,
          };
        }
        if (questionDataType === "boolean") {
          output[questionKey] = {
            data_type: "boolean",
            display_name,
          };
        }
        if (questionDataType === "number" || questionDataType === "integer") {
          output[questionKey] = {
            data_type: "number",
            display_name,
          };
        }
        if (
          questionDataType === "pick-list" ||
          questionDataType === "multi-pick-list"
        ) {
          output[questionKey] = {
            data_type: "pick-list",
            display_name,
            pick_list_options: questionDefinition.pick_list_values ?? [],
          };
        }
      }
    }
  );

  return output;
};

const filterBuilderFilterToAnswerFilter = (input: object): object[] => {
  const transformed: object[] = [];

  Object.keys(input).forEach((conditionKey) => {
    if (conditionKey === "$or" || conditionKey === "$and") {
      const transformedConditions = input[conditionKey]
        .map(filterBuilderFilterToAnswerFilter)
        .map((transformedArray: object[]) => transformedArray[0]);

      transformed.push({ [conditionKey]: transformedConditions });
    } else {
      transformed.push({
        question_id: conditionKey,
        "answer.value": input[conditionKey],
      });
    }
  });

  return transformed;
};

const WorkflowConfigurationTriggerFormCreation = ({
  triggerDefinition,
}: {
  triggerDefinition: WorkflowTriggerFormCreate;
}) => {
  const {
    setTriggerFormCreation,
    setTriggerFormCreationIsValid,
    getTriggerFormCreation,
  } = useWorkflowConfigurationFormStore();

  const storeValues = getTriggerFormCreation();

  const form = useForm<
    Pick<WorkflowTriggerFormCreate, "form_schema_id" | "answer_filter">
  >({
    values: triggerDefinition,
  });

  const handleValueChange = (
    key: "form_schema_id" | "answer_filter",
    value: unknown
  ) => {
    if (storeValues) {
      setTriggerFormCreation({
        ...storeValues,
        [key]: value,
      });
    }
  };

  const [isAnswerFilterEnabled, setIsAnswerFilterEnabled] = useState(
    !!triggerDefinition?.answer_filter
  );

  const { data: formSchemaData } = useGetOneFormSchema(
    {
      id: storeValues?.form_schema_id ?? "",
    },
    { enabled: !!storeValues?.form_schema_id }
  );

  const [answerFilter, setAnswerFilter] = useState<object>({});

  useDeepCompareEffectNoCheck(() => {
    if (triggerDefinition?.answer_filter)
      setAnswerFilter(
        answerFilterToFilterBuilderFilter(triggerDefinition.answer_filter)
      );
  }, [triggerDefinition?.answer_filter]);

  useEffect(() => {
    handleValueChange(
      "answer_filter",
      filterBuilderFilterToAnswerFilter(answerFilter)
    );
  }, [answerFilter]);

  const [fields, setFields] = useState<FilterBuilderFieldsType>({});

  useEffect(() => {
    if (formSchemaData?.data.config) {
      setFields(
        formSchemaConfigToFilterBuilderFields(formSchemaData.data.config)
      );
    }
  }, [formSchemaData?.data.config]);

  const { isVerifying, results } = useVerifyNewFormSchema({
    output: triggerDefinition.output,
    currentFormSchemaId: triggerDefinition.form_schema_id,
    selectedFormSchemaId: storeValues?.form_schema_id ?? "",
  });

  useEffect(() => {
    setTriggerFormCreationIsValid(
      form.formState.isValid && !isVerifying && results.length === 0
    );

    if (results.length > 0) {
      setIsAnswerFilterEnabled(false);
      setAnswerFilter({});
    }
  }, [
    form.formState.isValid,
    isVerifying,
    results.length,
    setTriggerFormCreationIsValid,
  ]);

  return (
    <>
      <Form {...form}>
        <FormSchemaDropdownInput
          name="form_schema_id"
          label="Form Template"
          isMulti={false}
          fetchFilters={{ status: "active" }}
          isRequired
          onChange={(newValue: string) => {
            setAnswerFilter({});
            handleValueChange("form_schema_id", newValue);
          }}
        />
      </Form>

      {results.length > 0 && (
        <>
          <Banner
            titleText="The newly selected template does not account for one or more question from the existing template."
            isDismissable={false}
            variant="error"
          />
          <Card className={cx("submitFormErrors")}>
            <div className={cx("errorQuestionsTitle")}>
              Questions Not Accounted For
            </div>
            {results.map((result) => (
              <KeyValueList
                variant="shaded"
                key={result.faultyKey + result.questionName}
                data={[
                  ...(result.sectionName
                    ? [
                        {
                          title: "Section",
                          value: result.sectionName,
                        },
                      ]
                    : []),
                  { title: "Question", value: result.questionName },
                  {
                    title: "Issue",
                    value: (
                      <span className={cx("errorText")}>
                        Empty question in {result.faultyKey}
                      </span>
                    ),
                  },
                ]}
              />
            ))}
          </Card>
        </>
      )}

      <Checkbox
        name=""
        value={isAnswerFilterEnabled}
        onChange={(newValue) => {
          setIsAnswerFilterEnabled(newValue);
          handleValueChange("answer_filter", newValue ? [] : undefined);
        }}
        label="Define specific trigger questions + answers rules"
      />

      {isAnswerFilterEnabled && (
        <FilterBuilder
          variant="vertical"
          filter={answerFilter}
          setFilter={setAnswerFilter}
          fields={fields}
          maxDepth={2}
        />
      )}
    </>
  );
};

export default WorkflowConfigurationTriggerFormCreation;
